"use client";

import { db, getClientFunctions } from "@/lib/firebase-client";
import type { AccountTier } from "@/lib/types";
import type { TierLimits } from "@ezrecipebox/contracts";
import type { User } from "firebase/auth";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

const USERS_COLLECTION = "users";

/**
 * Default account tier limits (fallback if database fetch fails)
 */
export const DEFAULT_TIER_LIMITS: Record<AccountTier, TierLimits> = {
  free: {
    tier: "free",
    maxRecipeBoxes: 5,
    maxMembersPerBox: 5,
    maxRecipesPerBox: 50,
    showAds: true,
  },
  full_pro: {
    tier: "full_pro",
    maxRecipeBoxes: null,
    maxMembersPerBox: null,
    maxRecipesPerBox: null,
    showAds: false,
  },
} as const;

/**
 * Cache for tier limits to avoid excessive database calls
 */
let tierLimitsCache: Record<AccountTier, TierLimits> | null = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch tier limits from the database
 * Uses caching to avoid excessive function calls
 */
export async function fetchTierLimitsFromDB(): Promise<Record<AccountTier, TierLimits>> {
  const now = Date.now();
  
  // Return cached limits if still valid
  if (tierLimitsCache && now - cacheTimestamp < CACHE_DURATION) {
    return tierLimitsCache;
  }

  try {
    const functions = getClientFunctions();
    if (!functions) {
      return DEFAULT_TIER_LIMITS;
    }

    const getTierLimits = httpsCallable<
      { tier?: AccountTier },
      { tierLimits: Record<AccountTier, TierLimits> | null }
    >(functions, "getTierLimits");

    const result = await getTierLimits({});
    const fetchedLimits = result.data.tierLimits;

    if (fetchedLimits) {
      // Merge with defaults to ensure all tiers are present
      tierLimitsCache = { ...DEFAULT_TIER_LIMITS, ...fetchedLimits };
      cacheTimestamp = now;
      return tierLimitsCache;
    }
  } catch (error) {
    console.error("Error fetching tier limits from database:", error);
  }

  return DEFAULT_TIER_LIMITS;
}

/**
 * Clear the tier limits cache (useful after admin updates)
 */
export function clearTierLimitsCache(): void {
  tierLimitsCache = null;
  cacheTimestamp = 0;
}

/**
 * Get the maximum number of recipes allowed for a given account tier
 * @param tier - The account tier
 */
export function getMaxRecipesForTier(
  tier: AccountTier | undefined,
): number {
  const normalizedTier = tier ?? "free";
  return DEFAULT_TIER_LIMITS[normalizedTier].maxRecipesPerBox ?? Infinity;
}

/**
 * Check if a user can create more recipes based on their account tier
 * @param tier - The account tier
 * @param currentRecipeCount - Current number of recipes
 */
export function canCreateRecipe(
  tier: AccountTier | undefined,
  currentRecipeCount: number,
): boolean {
  const maxRecipes = getMaxRecipesForTier(tier);
  return currentRecipeCount < maxRecipes;
}

/**
 * Update user's account tier
 */
export async function updateAccountTier(
  user: User,
  newTier: AccountTier,
): Promise<void> {
  if (!user) {
    throw new Error("User must be authenticated to update account tier");
  }

  const docRef = doc(db, USERS_COLLECTION, user.uid);
  await updateDoc(docRef, {
    accountTier: newTier,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Get account tier display names
 */
export function getTierDisplayName(tier: AccountTier | undefined): string {
  const normalizedTier = tier ?? "free";
  switch (normalizedTier) {
    case "free":
      return "Free";
    case "full_pro":
      return "Full Pro";
    default:
      return "Unknown";
  }
}

/**
 * Get account tier display information (with cached limits)
 * @param tier - The account tier
 * @param tierLimits - Optional pre-fetched tier limits
 */
export function getTierDisplayInfo(
  tier: AccountTier | undefined,
  tierLimits?: Record<AccountTier, TierLimits>,
) {
  const normalizedTier = tier ?? "free";
  const limits = tierLimits?.[normalizedTier] ?? DEFAULT_TIER_LIMITS[normalizedTier];

  return {
    name: getTierDisplayName(normalizedTier),
    description:
      normalizedTier === "free"
        ? "Ad-supported account with limited sharing"
        : "Unlimited recipes, no ads",
    ...limits,
  };
}

